import React, { useEffect, useMemo, useState } from 'react';
import { vehicleCatalogService, type VehicleCatalogOption } from '@/api/services/vehicleCatalog';
import { AppFormField, AppInput } from '@/ui';

type CatalogKind = 'make' | 'model' | 'trim';

type Props = {
  kind: CatalogKind;
  label: string;
  value: string;
  catalogId: string;
  parentMakeId?: string;
  parentModelId?: string;
  placeholder?: string;
  onChange: (next: { value: string; catalogId: string; custom: boolean }) => void;
};

export default function VehicleCatalogField({
  kind,
  label,
  value,
  catalogId,
  parentMakeId,
  parentModelId,
  placeholder,
  onChange,
}: Props) {
  const [options, setOptions] = useState<VehicleCatalogOption[]>([]);
  const [loading, setLoading] = useState(false);
  const listId = useMemo(() => `vehicle-catalog-${kind}`, [kind]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      try {
        let items: VehicleCatalogOption[] = [];
        if (kind === 'make') {
          items = await vehicleCatalogService.makes(value || undefined);
        } else if (kind === 'model') {
          items = await vehicleCatalogService.models(parentMakeId || undefined, value || undefined);
        } else {
          items = await vehicleCatalogService.trims(parentModelId || undefined, value || undefined);
        }
        if (!cancelled) setOptions(items);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [kind, value, parentMakeId, parentModelId]);

  const matched = options.find(
    (opt) => opt.id === catalogId || opt.name.toLowerCase() === value.trim().toLowerCase(),
  );
  const isCustom = Boolean(value.trim()) && !matched;

  return (
    <AppFormField
      label={label}
      helperText={loading ? 'Loading catalog…' : isCustom ? 'Custom value (not in catalog)' : undefined}
    >
      <AppInput
        list={listId}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          const nextValue = e.target.value;
          const nextMatch = options.find(
            (opt) => opt.name.toLowerCase() === nextValue.trim().toLowerCase(),
          );
          onChange({
            value: nextValue,
            catalogId: nextMatch?.id ?? '',
            custom: Boolean(nextValue.trim()) && !nextMatch,
          });
        }}
      />
      <datalist id={listId}>
        {options.map((opt) => (
          <option key={opt.id} value={opt.name} />
        ))}
      </datalist>
      {isCustom ? <span className="chip chip--muted">custom value</span> : null}
    </AppFormField>
  );
}
